import { useTranslation } from 'react-i18next'
import { useEffect, useState } from 'react'
import {
  Box, Text, Button, Badge, Group, Modal, Stack, Select,
  Textarea, ActionIcon, Tooltip, Center, Loader, Table,
  SimpleGrid, Paper, Tabs, NumberInput, TextInput, ScrollArea,
  Alert, Anchor, Card, Menu, Divider,
} from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useBreakpoint } from '../hooks/useBreakpoint'
import { notifications } from '@mantine/notifications'
import { modals } from '@mantine/modals'
import {
  IconPlus, IconCheck, IconX, IconArrowRight, IconTransfer, IconPrinter, IconSearch, IconInfoCircle, IconEdit, IconHistory, IconDotsVertical,
} from '@tabler/icons-react'
import {
  getTransfers, createTransfer, approveTransfer, rejectTransfer, cancelTransfer,
  getAssets, getInventoryItems, getLicenses, getDepartments, updateTransfer, scanLookupAsset, getTransferHistory, downloadTransferPdf,
} from '../api/client'
import { useAuth } from '../hooks/useAuth'
import AssetQuickView from '../components/assets/AssetQuickView'
import HistoryTimelineModal from '../components/HistoryTimelineModal'

const STATUS_COLOR: Record<string, string> = {
  PENDING: 'yellow', APPROVED: 'teal', REJECTED: 'red', CANCELLED: 'gray',
}
const TYPE_LABEL: Record<string, string> = {
  ASSET: 'Asset', INVENTORY: 'Consumable', LICENSE: 'License',
}

export default function TransferPage() {
  const { t, i18n } = useTranslation()
  const { isMobile, isTablet, isSmall } = useBreakpoint()
  const { roles, username, departments: userDepts } = useAuth()
  const isApprover = roles.includes('ADMIN') || roles.includes('ASSET_MANAGER')
  const userDept = userDepts?.[0] ?? ''

  const [transfers, setTransfers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [opened, { open, close }] = useDisclosure(false)
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false)
  const [historyTarget, setHistoryTarget] = useState<any>(null)
  const [historyData, setHistoryData] = useState<any[]>([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  // Form state
  const [type, setType] = useState<string>('ASSET')
  const [toDept, setToDept] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [selectedItems, setSelectedItems] = useState<{ id: number; code?: string; name: string; qty?: number; brand?: string; model?: string; serialNumber?: string }[]>([])
  const [qvAsset, setQvAsset] = useState<any>(null)
  const [qvOpen, setQvOpen] = useState(false)
  const openQv = (code: string) => scanLookupAsset(code).then(r => { if (r.data?.found) { setQvAsset(r.data); setQvOpen(true) } })
  const [saving, setSaving] = useState(false)
  const [assetSearch, setAssetSearch] = useState('')
  const [editingTransferId, setEditingTransferId] = useState<number | null>(null)
  const [transferModalKey, setTransferModalKey] = useState(0)

  // Reference data
  const [depts, setDepts] = useState<any[]>([])
  const [assets, setAssets] = useState<any[]>([])
  const [invItems, setInvItems] = useState<any[]>([])
  const [licenses, setLicenses] = useState<any[]>([])

  const load = async () => {
    setLoading(true)
    try {
      const [t, d, a, inv, lic] = await Promise.all([
        getTransfers(), getDepartments(),
        getAssets(), getInventoryItems(), getLicenses(),
      ])
      setTransfers(t.data)
      setDepts((d.data as any[]).filter((x: any) => x.code !== userDept && x.isActive !== false))
      setAssets((a.data as any[]).filter((x: any) => userDept ? x.departmentCode === userDept : true))
      setInvItems((inv.data as any[]).filter((x: any) => userDept ? x.departmentCode === userDept : true))
      setLicenses((lic.data as any[]).filter((x: any) => userDept ? x.departmentCode === userDept : true))
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const openCreate = () => {
    setTransferModalKey(k => k + 1)
    setEditingTransferId(null)
    setType('ASSET'); setToDept(null); setReason(''); setSelectedItems([]); setAssetSearch('')
    open()
  }

  const openEdit = (t: any) => {
    setTransferModalKey(k => k + 1)
    setEditingTransferId(t.id)
    setType(t.type ?? 'ASSET')
    setToDept(t.toDept ?? null)
    setReason(t.reason ?? '')
    setAssetSearch('')
    setSelectedItems((t.items ?? []).map((it: any) => ({
      id: it.id, code: it.code, name: it.name, qty: it.qty,
      brand: it.brand, model: it.model, serialNumber: it.serialNumber,
    })))
    open()
  }

  const getItemOptions = () => {
    if (type === 'ASSET') return assets.map(a => ({ value: String(a.id), label: `${a.assetCode} — ${a.name}`, item: a }))
    if (type === 'INVENTORY') return invItems.map(i => ({ value: String(i.id), label: `${i.sku} — ${i.name} (Stock: ${i.stockQty})`, item: i }))
    return licenses.map(l => ({ value: String(l.id), label: `${l.productName}`, item: l }))
  }

  const addItem = (v: string | null) => {
    if (!v) return
    const opt = getItemOptions().find(o => o.value === v)
    if (!opt) return
    const exists = selectedItems.find(i => i.id === opt.item.id)
    if (exists) return
    setSelectedItems(p => [...p, {
      id: opt.item.id,
      code: opt.item.assetCode ?? opt.item.sku ?? undefined,
      name: opt.item.name ?? opt.item.productName,
      qty: type === 'INVENTORY' ? 1 : undefined,
      brand: opt.item.brand,
      model: opt.item.model,
      serialNumber: opt.item.serialNumber,
    }])
  }

  const handleCreate = async () => {
    if (!toDept || selectedItems.length === 0) {
      notifications.show({ color: 'red', message: 'Select destination department and at least one item' })
      return
    }
    setSaving(true)
    try {
      if (editingTransferId) {
        await updateTransfer(editingTransferId, { toDept, items: selectedItems, reason: reason || undefined })
        notifications.show({ color: 'teal', message: 'Transfer request updated' })
      } else {
        await createTransfer({ type, toDept, items: selectedItems, reason: reason || undefined })
        notifications.show({ color: 'teal', message: 'Transfer request submitted — pending approval' })
      }
      close(); load()
    } catch (e: any) {
      notifications.show({ color: 'red', message: e.response?.data?.message ?? 'Error' })
    } finally { setSaving(false) }
  }

  const handleApprove = (tr: any) => {
    modals.openConfirmModal({
      title: t('transfer.approve','Approve Transfer'),
      children: <Text size="sm">Approve transfer #{tr.id} from <b>{tr.fromDept}</b> → <b>{tr.toDept}</b>? {t('transfer.approveDesc','Items will move immediately.')}</Text>,
      labels: { confirm: t('common.approve','Approve'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'teal' },
      onConfirm: async () => {
        await approveTransfer(tr.id)
        notifications.show({ color: 'teal', message: 'Transfer approved — items moved' })
        load()
      },
    })
  }

  const handleReject = (tr: any) => {
    modals.openConfirmModal({
      title: t('transfer.reject','Reject Transfer'),
      children: <Text size="sm">Reject transfer #{tr.id}?</Text>,
      labels: { confirm: t('common.reject','Reject'), cancel: t('common.cancel','Cancel') },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        await rejectTransfer(tr.id, {})
        notifications.show({ color: 'red', message: 'Transfer rejected' })
        load()
      },
    })
  }

  const handleCancel = (tr: any) => {
    modals.openConfirmModal({
      title: t('transfer.cancelRequest', 'Cancel Transfer'),
      children: <Text size="sm">Cancel transfer #{tr.id}?</Text>,
      labels: { confirm: t('transfer.cancelRequest','Cancel Request'), cancel: t('common.keep','Keep') },
      confirmProps: { color: 'gray' },
      onConfirm: async () => {
        await cancelTransfer(tr.id)
        notifications.show({ color: 'gray', message: 'Transfer cancelled' })
        load()
      },
    })
  }

  // ─── Transfer History ───────────────────────────────────────────────────────
  const handleOpenHistory = async (tr: any) => {
    setHistoryTarget(tr)
    setHistoryData([])
    openHistory()
    setLoadingHistory(true)
    try {
      const res = await getTransferHistory(tr.id)
      setHistoryData(res.data)
    } catch (_) {
      notifications.show({ color: 'red', message: t('transfer.historyLoadFailed', 'Failed to load history') })
    } finally { setLoadingHistory(false) }
  }

  const [search, setSearch] = useState('')

  const pending  = transfers.filter(tr => tr.status === 'PENDING')
  const mine     = transfers.filter(tr => tr.requestedBy === username)
  const approved = transfers.filter(tr => tr.status === 'APPROVED')
  const rejected = transfers.filter(tr => tr.status === 'REJECTED')
  const all      = transfers

  const [activeTab, setActiveTab] = useState('all')

  const cards = [
    { label: t('common.total','Total'), count: all.length,                                           color: '#64748b', tab: 'all'     },
    { label: t('transfer.pending','Pending Approval'), count: pending.length,                                       color: '#eab308', tab: 'pending' },
    { label: t('common.myRequests','My Requests'),      count: mine.length,                                          color: '#6366f1', tab: 'my'      },
    { label: t('common.approved','Approved'),         count: approved.length, color: '#14b8a6', tab: 'approved' },
    { label: t('transfer.rejected','Rejected'),        count: rejected.length, color: '#ef4444', tab: 'rejected' },
  ]

  const applySearch = (data: any[]) => {
    if (!search.trim()) return data
    const q = search.toLowerCase()
    return data.filter(t =>
      String(t.id).includes(q) ||
      `trf-${String(t.id).padStart(4, '0')}`.includes(q) ||
      t.fromDept?.toLowerCase().includes(q) ||
      t.toDept?.toLowerCase().includes(q) ||
      t.requestedBy?.toLowerCase().includes(q) ||
      t.status?.toLowerCase().includes(q) ||
      (t.items ?? []).some((i: any) => (i.code ?? i.name ?? '').toLowerCase().includes(q))
    )
  }

  const renderTable = (data: any[]) => {
    const filtered = applySearch(data)
    if (filtered.length === 0) {
      return <Center p="xl"><Text c="dimmed">{data.length === 0 ? t('transfer.noItems','No transfer requests') : t('transfer.noSearchResults', 'No matching results')}</Text></Center>
    }

    if (isMobile) {
      // ── มือถือ: การ์ดตาม pattern เดียวกับ Configuration/License/Assets/Borrow — ของเดิมเป็น
      // ตารางเว็บ 9 คอลัมน์ล้วนๆ ไม่มี mobile view เลยสักนิด ปุ่มจัดการรวมเป็นเมนู ⋮ ที่มี label
      // กำกับทุกปุ่มแทนไอคอนเล็กที่พึ่ง tooltip อย่างเดียว (ใช้ไม่ได้จริงบนจอสัมผัส)
      return (
        <Stack gap="xs" p="xs">
          {filtered.map(tr => {
            const canEdit = tr.status === 'PENDING'
            const canApproveReject = isApprover && tr.status === 'PENDING'
            const canCancel = tr.requestedBy === username && tr.status === 'PENDING'
            return (
              <Card key={tr.id} withBorder radius="lg" p="md">
                <Group justify="space-between" align="flex-start" wrap="nowrap" gap="xs">
                  <Box style={{ minWidth: 0, flex: 1 }}>
                    <Text size="sm" fw={600} style={{ color: '#0f172a' }}>TRF-{String(tr.id).padStart(4, '0')}</Text>
                    <Group gap={4}>
                      <Text size="xs" c="dimmed">{tr.fromDept}</Text>
                      <IconArrowRight size={11} color="#94a3b8" />
                      <Text size="xs" c="dimmed">{tr.toDept}</Text>
                    </Group>
                  </Box>
                  <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                    <Badge size="sm" color={STATUS_COLOR[tr.status] ?? 'gray'} variant="light">{tr.status}</Badge>
                    <Menu shadow="md" width={200} position="bottom-end">
                      <Menu.Target>
                        <ActionIcon variant="subtle" color="gray" size="lg"><IconDotsVertical size={18} /></ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        {canEdit && (
                          <Menu.Item leftSection={<IconEdit size={15} />} onClick={() => openEdit(tr)}>
                            {t('transfer.editRequest','Edit Request')}
                          </Menu.Item>
                        )}
                        {canApproveReject && (
                          <>
                            <Menu.Item leftSection={<IconCheck size={15} color="var(--mantine-color-teal-6)" />} onClick={() => handleApprove(tr)}>
                              {t('common.approve','Approve')}
                            </Menu.Item>
                            <Menu.Item leftSection={<IconX size={15} color="var(--mantine-color-red-6)" />} onClick={() => handleReject(tr)}>
                              {t('common.reject','Reject')}
                            </Menu.Item>
                          </>
                        )}
                        {canCancel && (
                          <Menu.Item leftSection={<IconX size={15} />} onClick={() => handleCancel(tr)}>
                            {t('common.cancel','Cancel')}
                          </Menu.Item>
                        )}
                        <Menu.Item leftSection={<IconPrinter size={15} />} onClick={async () => {
                          try {
                            const res = await downloadTransferPdf(tr.id, i18n.language === 'th' ? 'th' : 'en')
                            const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                            window.open(url, '_blank')
                            setTimeout(() => URL.revokeObjectURL(url), 30_000)
                          } catch { notifications.show({ color: 'red', message: t('transfer.pdfGenerationFailed', 'Failed to generate PDF') }) }
                        }}>
                          {t('common.print','Print')}
                        </Menu.Item>
                        <Menu.Item leftSection={<IconHistory size={15} />} onClick={() => handleOpenHistory(tr)}>
                          {t('transfer.viewHistory','History')}
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>
                </Group>

                <Divider my={8} />

                <Group justify="space-between" align="flex-start" wrap="wrap" gap="xs">
                  <Group gap={6}>
                    <Badge size="sm" variant="light" color="blue">{TYPE_LABEL[tr.type] ?? tr.type}</Badge>
                    <Text size="xs" c="dimmed">{tr.requestedBy}</Text>
                  </Group>
                  <Text size="xs" c="dimmed">{tr.createdAt ? new Date(tr.createdAt).toLocaleDateString('en-GB') : '-'}</Text>
                </Group>

                <Text size="xs" fw={600} c="dimmed" mt={8} mb={4}>
                  {t('transfer.itemsCount', '{{count}} item(s)', { count: (tr.items ?? []).length })}
                </Text>
                {tr.type === 'ASSET' ? (
                  <Group gap={6} wrap="wrap">
                    {(tr.items ?? []).slice(0, 4).map((i: any, idx: number) => (
                      <Badge key={idx} size="xs" variant="dot" color="teal" style={{ cursor: 'pointer' }}
                        onClick={() => openQv(i.code)}>
                        {i.code ?? i.name}
                      </Badge>
                    ))}
                    {(tr.items ?? []).length > 4 && <Text size="xs" c="dimmed">+{tr.items.length - 4} {t('common.more', 'more')}</Text>}
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed" lineClamp={2}>
                    {(tr.items ?? []).map((i: any) => i.code ?? i.name).join(', ')}
                  </Text>
                )}
              </Card>
            )
          })}
        </Stack>
      )
    }

    return (
      <ScrollArea><Table highlightOnHover>
        <Table.Thead style={{ background: '#f8fafc' }}>
          <Table.Tr>
            {['#', 'Type', 'From', 'To', 'Items', 'Requested By', 'Date', 'Status', ''].map(h => (
              <Table.Th key={h} style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' }}>{h}</Table.Th>
            ))}
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map(tr => (
            <Table.Tr key={tr.id}>
              <Table.Td><Text size="sm" fw={700}>TRF-{String(tr.id).padStart(4, '0')}</Text></Table.Td>
              <Table.Td><Badge size="xs" variant="light" color="blue">{TYPE_LABEL[tr.type] ?? tr.type}</Badge></Table.Td>
              <Table.Td><Text size="sm">{tr.fromDept}</Text></Table.Td>
              <Table.Td>
                <Group gap={4}>
                  <IconArrowRight size={12} color="#94a3b8" />
                  <Text size="sm" fw={500}>{tr.toDept}</Text>
                </Group>
              </Table.Td>
              <Table.Td>
                <Text size="sm">{(tr.items ?? []).length} item(s)</Text>
                {tr.type === 'ASSET' ? (
                  <Group gap={4} wrap="wrap">
                    {(tr.items ?? []).slice(0, 2).map((i: any, idx: number) => (
                      <Text key={idx} size="xs" style={{ cursor: 'pointer', color: '#0d9488', textDecoration: 'underline dotted' }}
                        onClick={() => openQv(i.code)}>
                        {i.code ?? i.name}
                      </Text>
                    ))}
                    {(tr.items ?? []).length > 2 && <Text size="xs" c="dimmed">+{tr.items.length - 2} more</Text>}
                  </Group>
                ) : (
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {(tr.items ?? []).map((i: any) => i.code ?? i.name).join(', ')}
                  </Text>
                )}
              </Table.Td>
              <Table.Td><Text size="sm">{tr.requestedBy}</Text></Table.Td>
              <Table.Td>
                <Text size="xs" c="dimmed">
                  {tr.createdAt ? new Date(tr.createdAt).toLocaleDateString('en-GB') : '-'}
                </Text>
              </Table.Td>
              <Table.Td>
                <Badge color={STATUS_COLOR[tr.status] ?? 'gray'} variant="light" size="sm">{tr.status}</Badge>
              </Table.Td>
              <Table.Td>
                <Group gap={4} wrap="nowrap">
                  {tr.status === 'PENDING' && (
                    <Tooltip label={t("transfer.editRequest","Edit Request")}>
                      <ActionIcon color="blue" variant="subtle" size="sm" onClick={() => openEdit(tr)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  {isApprover && tr.status === 'PENDING' && (
                    <>
                      <Tooltip label={t("common.approve","Approve")}>
                        <ActionIcon color="teal" variant="light" size="sm" onClick={() => handleApprove(tr)}>
                          <IconCheck size={14} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label={t("common.reject","Reject")}>
                        <ActionIcon color="red" variant="light" size="sm" onClick={() => handleReject(tr)}>
                          <IconX size={14} />
                        </ActionIcon>
                      </Tooltip>
                    </>
                  )}
                  {tr.requestedBy === username && tr.status === 'PENDING' && (
                    <Tooltip label={t("common.cancel","Cancel")}>
                      <ActionIcon color="gray" variant="subtle" size="sm" onClick={() => handleCancel(tr)}>
                        <IconX size={14} />
                      </ActionIcon>
                    </Tooltip>
                  )}
                  <Tooltip label={t("common.print", "Print")}>
                    <ActionIcon color="gray" variant="subtle" size="sm" onClick={async () => {
                      try {
                        const res = await downloadTransferPdf(tr.id, i18n.language === 'th' ? 'th' : 'en')
                        const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
                        window.open(url, '_blank')
                        setTimeout(() => URL.revokeObjectURL(url), 30_000)
                      } catch { notifications.show({ color: 'red', message: t('transfer.pdfGenerationFailed', 'Failed to generate PDF') }) }
                    }}>
                      <IconPrinter size={14} />
                    </ActionIcon>
                  </Tooltip>
                  <Tooltip label={t("transfer.viewHistory", "History")}>
                    <ActionIcon color="indigo" variant="subtle" size="sm" onClick={() => handleOpenHistory(tr)}>
                      <IconHistory size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table></ScrollArea>
    )
  }

  return (
    <Box p={isSmall ? 16 : 32}>
      <Group justify="space-between" mb="lg">
        <Box>
          <Text style={{ fontFamily: 'Inter, sans-serif', fontSize: isSmall ? 20 : 24, fontWeight: 600, letterSpacing: '-0.3px' }}>
            {t("transfer.title","Transfer Requests")}
          </Text>
          <Text size="sm" c="dimmed">{t('transfer.subtitle','Request department transfers — requires ASSET_MANAGER approval')}</Text>
          <Alert variant="light" color="violet" radius="md" mt={6} py={6}
            icon={<IconInfoCircle size={15} />}
            styles={{ body: { gap: 4 } }}>
            <Text size="xs" fw={600}>{t('transfer.policyHint')}</Text>
            <Text size="xs" c="dimmed">{t('transfer.policyHandoverHint')} <Anchor href="/handover" size="xs" fw={600}>{t("transfer.handoverLink","Handover")}</Anchor></Text>
          </Alert>
        </Box>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}
          style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)', fontWeight: 500 }}>
          {t('transfer.newTransfer','New Transfer')}
        </Button>
      </Group>

      {/* Summary cards */}
      {!loading && (
        <Group gap="xs" mb="lg" wrap="wrap" justify="flex-start">
          {cards.map(c => (
            <Paper key={c.label} withBorder radius="lg" p="sm"
              onClick={() => {
                if (c.tab === 'pending' && !isApprover) return
                setActiveTab(c.tab)
              }}
              style={{
                borderLeft: `4px solid ${c.color}`,
                cursor: (c.tab === 'pending' && !isApprover) ? 'default' : 'pointer',
                transition: 'box-shadow 0.15s',
                width: isSmall ? 'calc(50% - 4px)' : 150, flexShrink: 0,
              }}
              onMouseEnter={e => { if (!(c.tab === 'pending' && !isApprover)) (e.currentTarget as HTMLElement).style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = 'none' }}
            >
              <Text size="xs" c="dimmed" fw={600} style={{ textTransform: 'uppercase', letterSpacing: '0.3px', lineHeight: 1.2 }}>{c.label}</Text>
              <Text size="lg" fw={800} style={{ color: c.color, fontFamily: 'Inter, sans-serif' }}>{c.count}</Text>
            </Paper>
          ))}
        </Group>
      )}

      <Box style={{ background: '#fff', borderRadius: 16, border: '1px solid #ebebeb', overflow: 'auto' }}>
        <Box style={{ padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
          <TextInput
            placeholder="ค้นหา #ID แผนก ผู้ขอ สถานะ..."
            value={search} onChange={e => setSearch(e.target.value)}
            leftSection={<IconSearch size={14} />}
            size="sm" style={{ maxWidth: 320 }}
          />
        </Box>
        {loading ? <Center p="xl"><Loader color="teal" /></Center> : (
          <Tabs value={activeTab} onChange={v => setActiveTab(v ?? 'all')}>
            {isApprover && <Tabs.Panel value="pending" pt="sm">{renderTable(pending)}</Tabs.Panel>}
            <Tabs.Panel value="my" pt="sm">{renderTable(mine)}</Tabs.Panel>
            <Tabs.Panel value="approved" pt="sm">{renderTable(approved)}</Tabs.Panel>
            <Tabs.Panel value="rejected" pt="sm">{renderTable(rejected)}</Tabs.Panel>
            <Tabs.Panel value="all" pt="sm">{renderTable(all)}</Tabs.Panel>
          </Tabs>
        )}
      </Box>

      {/* Create Transfer Modal */}
      <Modal opened={opened} onClose={close} size={isSmall ? '100%' : 'md'} fullScreen={isSmall} radius={isMobile ? 0 : 'lg'}
        key={transferModalKey}
        title={<Group gap={8}><IconTransfer size={18} /><Text fw={600} style={{ fontFamily: 'Inter, sans-serif' }}>{editingTransferId ? `${t('transfer.editTransfer','Edit Transfer')} #${editingTransferId}` : t('transfer.newTransferRequest','New Transfer Request')}</Text></Group>}>
        <Stack gap="sm">
          <Text size="xs" c="dimmed">From: <b>{userDept || 'Global'}</b></Text>
          <Select label={t("transfer.transferType","Transfer Type")} value={type} onChange={v => { setType(v ?? 'ASSET'); setSelectedItems([]) }}
            data={[
              { value: 'ASSET', label: '🖥️ Asset' },
              { value: 'INVENTORY', label: '📦 Consumable Item' },
              { value: 'LICENSE', label: '🔑 Software License' },
            ]} />
          <Select label={t("transfer.destDept","Destination Department *")}
            placeholder={t("transfer.selectTargetDept","Select target department...")}
            data={depts.map(d => ({ value: d.code, label: d.name ?? d.code }))}
            value={toDept} onChange={setToDept} searchable clearable />
          <Box>
            <Text size="sm" fw={500} mb={6}>{t("transfer.selectItems","Select Items *")}</Text>
            {type === 'ASSET' ? (
              /* ── Asset: visual grid picker ── */
              <Box>
                {/* Sticky selected */}
                {selectedItems.length > 0 && (
                  <Box style={{ background: '#f0fdf4', border: '1.5px solid #0d9488', borderRadius: 10, padding: '10px 12px', marginBottom: 8 }}>
                    <Group justify="space-between" mb={6}>
                      <Text size="xs" fw={700} c="teal">{selectedItems.length} asset(s) selected</Text>
                      <Text size="xs" c="dimmed" style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setSelectedItems([])}>{t("common.clearAll","Clear all")}</Text>
                    </Group>
                    <Group gap={6} wrap="wrap">
                      {selectedItems.map(item => (
                        <Badge key={item.id} size="sm" color="teal" variant="light"
                          rightSection={
                            <Box style={{ cursor: 'pointer', marginLeft: 4 }}
                              onClick={() => setSelectedItems(p => p.filter(x => x.id !== item.id))}>✕</Box>
                          }>{item.code ?? item.name}</Badge>
                      ))}
                    </Group>
                  </Box>
                )}
                {/* Search */}
                <TextInput
                  placeholder={t("transfer.searchByNameSerial","Search by name or serial number...")}
                  size="xs" leftSection={<IconSearch size={13} />}
                  value={assetSearch} onChange={e => setAssetSearch(e.target.value)}
                  radius="md" mb={6}
                />
                {/* Grid */}
                <Box style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #ebebeb', borderRadius: 10, padding: 8 }}>
                  {assets.filter(a => {
                    if (a.status !== 'AVAILABLE') return false
                    if (selectedItems.some(s => s.id === a.id)) return false // hide selected ones from grid
                    const q = assetSearch.toLowerCase()
                    return !q || a.name?.toLowerCase().includes(q) || a.serialNumber?.toLowerCase().includes(q) || a.assetCode?.toLowerCase().includes(q)
                  }).length === 0 ? (
                    <Center p="md"><Text size="sm" c="dimmed">No available assets{assetSearch ? ` matching "${assetSearch}"` : ''}</Text></Center>
                  ) : (
                    <SimpleGrid cols={2} spacing="xs">
                      {assets.filter(a => {
                        if (a.status !== 'AVAILABLE') return false
                        if (selectedItems.some(s => s.id === a.id)) return false
                        const q = assetSearch.toLowerCase()
                        return !q || a.name?.toLowerCase().includes(q) || a.serialNumber?.toLowerCase().includes(q) || a.assetCode?.toLowerCase().includes(q)
                      }).map((a: any) => (
                        <Box key={a.id}
                          onClick={() => setSelectedItems(p => [...p, { id: a.id, code: a.assetCode, name: a.name, brand: a.brand, model: a.model, serialNumber: a.serialNumber }])}
                          style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 9px',
                            borderRadius: 8, cursor: 'pointer', border: '1.5px solid transparent',
                            background: '#fafafa', transition: 'all .1s' }}
                          onMouseEnter={e => (e.currentTarget.style.border = '1.5px solid #0d9488')}
                          onMouseLeave={e => (e.currentTarget.style.border = '1.5px solid transparent')}>
                          <Box style={{ minWidth: 0, flex: 1 }}>
                            <Text size="xs" fw={700}>{a.assetCode}</Text>
                            <Text size="xs" lineClamp={1}>{a.name}</Text>
                            <Text size="xs" c="dimmed">{a.serialNumber}</Text>
                          </Box>
                        </Box>
                      ))}
                    </SimpleGrid>
                  )}
                </Box>
              </Box>
            ) : (
              /* ── Inventory / License: original Select dropdown ── */
              <Box>
                <Select placeholder={t("inventory.searchAddItemPlaceholder","Search and add item...")}
                  searchable clearable
                  data={getItemOptions().map(o => ({ value: o.value, label: o.label }))}
                  onChange={addItem}
                  value={null}
                  nothingFoundMessage={t("transfer.noItemsInDept","No items in your department")}
                />
                {selectedItems.length > 0 && (
                  <Stack gap={4} mt={8}>
                    {selectedItems.map((item, i) => (
                      <Group key={i} justify="space-between" p="xs"
                        style={{ background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <Box style={{ minWidth: 0, flex: 1 }}>
                          <Text size="sm" fw={500} truncate>{item.name}</Text>
                          {item.code && <Text size="xs" c="dimmed">{item.code}</Text>}
                        </Box>
                        {type === 'INVENTORY' && (
                          <NumberInput size="xs" style={{ width: 80 }} min={1} value={item.qty ?? 1}
                            onChange={v => setSelectedItems(p => p.map((x, j) => j === i ? { ...x, qty: Number(v) || 1 } : x))} />
                        )}
                        <ActionIcon variant="subtle" color="red" size="sm"
                          onClick={() => setSelectedItems(p => p.filter((_, j) => j !== i))}>
                          <IconX size={13} />
                        </ActionIcon>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Box>
            )}
          </Box>
          <Textarea label={t("transfer.reasonNotes","Reason / Notes")} placeholder={t("transfer.explainWhy","Explain why this transfer is needed...")}
            value={reason} onChange={e => setReason(e.target.value)} rows={3} />
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={close}>{t("transfer.cancel","Cancel")}</Button>
            <Button loading={saving} onClick={handleCreate}
              style={{ background: 'var(--brand-primary, #0f172a)', color: 'var(--brand-on-primary, #fff)', border: '1px solid var(--brand-border, transparent)' }}>
              {t('transfer.submitRequest','Submit Request')}
            </Button>
          </Group>
        </Stack>
      </Modal>

      <HistoryTimelineModal
        opened={historyOpened}
        onClose={closeHistory}
        title={`${t('transfer.history', 'Transfer History')} ${historyTarget ? `#${historyTarget.id}` : ''}`}
        data={historyData}
        loading={loadingHistory}
        typeConfig={{
          TRANSFER_REQUESTED: { color: 'blue', label: t('transfer.hist.requested', 'Request Created'), icon: '📝' },
          TRANSFER_APPROVED:  { color: 'teal', label: t('transfer.hist.approved', 'Approved'),          icon: '✅' },
          TRANSFER_REJECTED:  { color: 'red',  label: t('transfer.hist.rejected', 'Rejected'),           icon: '❌' },
        }}
      />

      <AssetQuickView asset={qvAsset} opened={qvOpen} onClose={() => setQvOpen(false)} />
    </Box>
  )
}
